import React, { useState, useMemo, useEffect } from 'react';
import { ExpenseEntry, BUSINESS_CATEGORIES } from '../types';

interface BudgetItem {
  category: string;
  amount: number;
}

interface BudgetManagerProps {
  expenses: ExpenseEntry[];
  onSaveBudgets: (budgets: BudgetItem[]) => void;
  savedBudgets?: BudgetItem[];
}

const BudgetManager: React.FC<BudgetManagerProps> = ({ 
  expenses, 
  onSaveBudgets,
  savedBudgets = []
}) => {
  const [budgets, setBudgets] = useState<BudgetItem[]>(savedBudgets);
  const [isEditing, setIsEditing] = useState(false);
  const [newBudgetCategory, setNewBudgetCategory] = useState(BUSINESS_CATEGORIES[0]);
  const [newBudgetAmount, setNewBudgetAmount] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Initialize with saved budgets if available
  useEffect(() => {
    if (savedBudgets.length > 0) {
      setBudgets(savedBudgets);
    }
  }, [savedBudgets]);

  // Calculate totals for each category
  const categoryTotals = expenses.reduce((acc, expense) => {
    const category = expense.category;
    acc[category] = (acc[category] || 0) + expense.amount;
    return acc;
  }, {} as Record<string, number>);

  // Calculate spending by category
  const spendingByCategory = useMemo(() => {
    const result: Record<string, number> = {};
    
    // Initialize all categories to zero
    BUSINESS_CATEGORIES.forEach(category => {
      result[category] = 0;
    });
    
    // Sum expenses by category
    expenses.forEach(expense => {
      if (result[expense.category] !== undefined) {
        result[expense.category] += expense.amount;
      } else {
        result[expense.category] = expense.amount;
      }
    });
    
    return result;
  }, [expenses]);

  // Total budget and spending
  const totalBudget = useMemo(() => 
    budgets.reduce((sum, item) => sum + item.amount, 0),
    [budgets]
  );
  
  const totalSpending = useMemo(() => 
    Object.values(spendingByCategory).reduce((sum, amount) => sum + amount, 0),
    [spendingByCategory]
  );

  const handleAddBudget = () => {
    if (!newBudgetAmount) return;
    
    const amount = parseFloat(newBudgetAmount);
    if (isNaN(amount) || amount <= 0) return;
    
    // Check if budget already exists for this category
    const existingIndex = budgets.findIndex(b => b.category === newBudgetCategory);
    
    if (existingIndex >= 0) {
      // Update existing budget
      const updatedBudgets = [...budgets];
      updatedBudgets[existingIndex] = { 
        ...updatedBudgets[existingIndex], 
        amount 
      };
      setBudgets(updatedBudgets);
      onSaveBudgets(updatedBudgets);
    } else {
      // Add new budget
      const newBudget: BudgetItem = {
        category: newBudgetCategory,
        amount
      };
      const updatedBudgets = [...budgets, newBudget];
      setBudgets(updatedBudgets);
      onSaveBudgets(updatedBudgets);
    }
    
    // Reset form
    setNewBudgetAmount('');
    setNewBudgetCategory(BUSINESS_CATEGORIES[0]);
    setShowAddForm(false);
  };

  const handleRemoveBudget = (category: string) => {
    const updatedBudgets = budgets.filter(b => b.category !== category);
    setBudgets(updatedBudgets);
    onSaveBudgets(updatedBudgets);
  };

  const handleEditBudgets = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // Save changes when done editing
      onSaveBudgets(budgets);
    }
  };

  const getBudgetStatus = (category: string): 'under' | 'near' | 'over' | 'none' => {
    const budget = budgets.find(b => b.category === category);
    if (!budget) return 'none';
    
    const spent = categoryTotals[category] || 0;
    const percentage = (spent / budget.amount) * 100;
    
    if (percentage >= 100) return 'over';
    if (percentage >= 80) return 'near';
    return 'under';
  };

  const getAvailableCategoriesForBudget = () => {
    const existingBudgetCategories = budgets.map(b => b.category);
    return BUSINESS_CATEGORIES.filter(category => !existingBudgetCategories.includes(category));
  };

  // Handle budget item change
  const handleBudgetChange = (category: string, amount: number) => {
    setBudgets(prev => {
      const existingIndex = prev.findIndex(item => item.category === category);
      if (existingIndex >= 0) {
        // Update existing item
        const newBudgets = [...prev];
        newBudgets[existingIndex] = { ...newBudgets[existingIndex], amount };
        return newBudgets;
      } else {
        // Add new item
        return [...prev, { category, amount }];
      }
    });
  };

  // Get budget amount for a category
  const getBudgetForCategory = (category: string): number => {
    const budgetItem = budgets.find(item => item.category === category);
    return budgetItem ? budgetItem.amount : 0;
  };

  // Save budget changes
  const handleSave = () => {
    onSaveBudgets(budgets);
    setIsEditing(false);
  };

  // Cancel editing
  const handleCancel = () => {
    setBudgets(savedBudgets);
    setIsEditing(false);
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate percentage of budget used
  const calculatePercentage = (spent: number, budgeted: number): number => {
    if (budgeted === 0) return 0;
    return (spent / budgeted) * 100;
  };

  // Get appropriate color class based on percentage
  const getColorClass = (percentage: number): string => {
    if (percentage >= 100) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Budget Management</h3>
        <div>
          {isEditing ? (
            <div className="space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Budgets
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Edit Budgets
            </button>
          )}
        </div>
      </div>

      <div className="bg-gray-100 p-4 rounded-md">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <span className="text-sm text-gray-500">Total Budget:</span>
            <div className="text-xl font-semibold">{formatCurrency(totalBudget)}</div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Total Spending:</span>
            <div className={`text-xl font-semibold ${totalSpending > totalBudget ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(totalSpending)}
            </div>
          </div>
        </div>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                Overall Progress
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold inline-block text-blue-600">
                {totalBudget > 0 ? `${Math.min(Math.round((totalSpending / totalBudget) * 100), 100)}%` : '0%'}
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
            <div
              style={{ width: totalBudget > 0 ? `${Math.min((totalSpending / totalBudget) * 100, 100)}%` : '0%' }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                totalSpending > totalBudget ? 'bg-red-500' : 'bg-blue-500'
              }`}
            ></div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Budget
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actual Spending
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Difference
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Used
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {BUSINESS_CATEGORIES.map((category) => {
              const budgetAmount = getBudgetForCategory(category);
              const spentAmount = spendingByCategory[category] || 0;
              const difference = budgetAmount - spentAmount;
              const percentage = calculatePercentage(spentAmount, budgetAmount);
              const colorClass = getColorClass(percentage);

              return (
                <tr key={category}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={budgetAmount}
                        onChange={(e) => handleBudgetChange(category, parseFloat(e.target.value) || 0)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    ) : (
                      formatCurrency(budgetAmount)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {formatCurrency(spentAmount)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${difference >= 0 ? 'text-green-600' : 'text-red-600'} text-right font-medium`}>
                    {difference >= 0 ? '+' : ''}{formatCurrency(difference)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {budgetAmount > 0 ? (
                      <div className="text-sm font-medium">
                        <span className={colorClass}>{percentage.toFixed(1)}%</span>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className={`h-2.5 rounded-full ${colorClass.replace('text-', 'bg-')}`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">N/A</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BudgetManager; 